import { Box, Button, Chip, IconButton, Modal, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { FiClock, FiX } from "react-icons/fi";
import { useSaleDue, DueSaleInterface } from "./SaleDueContext";
import SaleConfirmPaymentModal from "./SaleConfirmPaymentModal";

const formatCurrency = (value: string | number) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// due_date chega como campo DATE (sem hora). Monta a data pelos componentes
// pra não deixar o navegador interpretar como UTC e exibir o dia anterior
// em fusos negativos (mesmo cuidado de PendingPaymentPopup).
const formatDate = (dateOnly: string) => {
  const [year, month, day] = (dateOnly ?? "").split("-").map(Number);
  if (!year || !month || !day) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
};

/**
 * Sino de vendas a prazo vencendo/vencidas — mesmo espírito de
 * PendingPaymentPopup, listando as vendas retornadas por /sales/check-due
 * com um botão de confirmar recebimento por linha.
 */
const SaleDuePopup: React.FC = () => {
  const { dueSales, modalOpen: open, closeModal, refresh } = useSaleDue();
  const [confirmTarget, setConfirmTarget] = useState<DueSaleInterface | null>(null);

  const total = dueSales.reduce((sum, s) => sum + Number(s.amount || 0), 0);

  return (
    <>
      <Modal open={open} onClose={() => closeModal()}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            color: "text.primary",
            borderRadius: "14px",
            boxShadow: 24,
            width: { xs: "92vw", sm: 460 },
            maxWidth: 460,
            maxHeight: "90vh",
            overflowY: "auto",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              p: 2.5,
              background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FiClock size={26} />
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                  Venda a prazo vencendo
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {dueSales.length === 1
                    ? "1 venda aguardando confirmação"
                    : `${dueSales.length} vendas aguardando confirmação`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => closeModal()} size="small" sx={{ color: "#fff" }}>
              <FiX />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              {dueSales.map((s) => (
                <Box
                  key={s.financial_entry_id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "10px",
                    p: 1.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      Venda #{s.sale_number}
                      {s.client_name ? ` — ${s.client_name}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Venc.: {formatDate(s.due_date)}
                      {s.status === "OVERDUE" && (
                        <Chip label="Vencida" color="error" size="small" sx={{ ml: 1, height: 20 }} />
                      )}
                    </Typography>
                    <Typography fontWeight={800} sx={{ mt: 0.5 }}>
                      {formatCurrency(s.amount)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => setConfirmTarget(s)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Confirmar
                  </Button>
                </Box>
              ))}
            </Stack>

            {dueSales.length > 1 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                <Typography color="text.secondary">Total em aberto</Typography>
                <Typography fontWeight={800}>{formatCurrency(total)}</Typography>
              </Box>
            )}

            <Box sx={{ mt: 2.5, textAlign: "right" }}>
              <Button onClick={() => closeModal()}>Ver depois</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <SaleConfirmPaymentModal
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        sale={confirmTarget}
        onConfirmed={() => refresh()}
      />
    </>
  );
};

export default SaleDuePopup;
